import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";

const OpenbravoLogViewer = () => {
  const [logEntries, setLogEntries] = useState([]);
  const [visibleCount, setVisibleCount] = useState(50);
  const [filter, setFilter] = useState("ALL");
  const [isLoading, setIsLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [autoScroll, setAutoScroll] = useState(true);
  const logContainerRef = useRef(null);

  /** ---- Parse Log Entries ---- **/
  const parseLogEntries = useCallback((text) => {
    const lines = text.trim().split("\n");
    const timestampRegex = /^(\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2},\d{3})/;

    const entries = [];
    let currentEntry = [];
    let entryCount = 0;

    for (let line of lines) {
      if (timestampRegex.test(line)) {
        if (currentEntry.length > 0) {
          entries.push({
            id: entryCount++,
            text: currentEntry.join("\n"),
            timestamp: currentEntry[0].match(timestampRegex)?.[1] || "",
            level:
              currentEntry[0].includes("ERROR")
                ? "ERROR"
                : currentEntry[0].includes("WARN")
                ? "WARN"
                : currentEntry[0].includes("DEBUG")
                ? "DEBUG"
                : "INFO",
          });
        }
        currentEntry = [line];
      } else {
        currentEntry.push(line);
      }
    }

    if (currentEntry.length > 0) {
      entries.push({
        id: entryCount,
        text: currentEntry.join("\n"),
        timestamp: currentEntry[0].match(timestampRegex)?.[1] || "",
        level:
          currentEntry[0].includes("ERROR")
            ? "ERROR"
            : currentEntry[0].includes("WARN")
            ? "WARN"
            : currentEntry[0].includes("DEBUG")
            ? "DEBUG"
            : "INFO",
      });
    }

    return entries;
  }, []);

  /** ---- Fetch Logs ---- **/
  const fetchLog = useCallback(async () => {
    try {
      const response = await fetch("/logs/openbravo_log.txt", {
        cache: "no-store",
        headers: {
          "Cache-Control": "no-cache, no-store, must-revalidate",
          Pragma: "no-cache",
        },
      });
      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
      const text = await response.text();

      if (!text.trim()) {
        setLogEntries([]);
        setIsLoading(false);
        return;
      }

      const entries = parseLogEntries(text);
      setLogEntries(entries);
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching Openbravo log:", error);
      setLogEntries([
        {
          id: 0,
          text: "⚠️ Unable to load log file. Check server path or permissions.",
          level: "ERROR",
          timestamp: new Date().toISOString().replace("T", " ").substring(0, 23),
        },
      ]);
      setIsLoading(false);
    }
  }, [parseLogEntries]);

  useEffect(() => {
    fetchLog();
    let interval;
    if (autoRefresh) interval = setInterval(fetchLog, 5000);
    return () => clearInterval(interval);
  }, [fetchLog, autoRefresh]);

  /** ---- Filtering ---- **/
  const filteredEntries = useMemo(() => {
    let filtered = logEntries;
    if (filter !== "ALL") filtered = filtered.filter((e) => e.level === filter);
    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      filtered = filtered.filter((e) => e.text.toLowerCase().includes(lower));
    }
    return filtered.slice(-visibleCount).reverse();
  }, [logEntries, filter, searchTerm, visibleCount]);

  useEffect(() => {
    if (autoScroll && logContainerRef.current) {
      logContainerRef.current.scrollTop = 0;
    }
  }, [filteredEntries, autoScroll]);

  /** ---- Helpers ---- **/
  const getLevelColor = (level) =>
    ({
      ERROR: "#ff0000", // Bright red for ERROR
      WARN: "#ff9900",  // Orange for WARN
      INFO: "#006600",   // Dark green for INFO
      DEBUG: "#0066cc",  // Blue for DEBUG
    }[level] || "#006600");

  const highlightText = (text, search) => {
    if (!search) return text;
    const regex = new RegExp(`(${search})`, "gi");
    const parts = text.split(regex);
    return parts.map((part, index) =>
      part.toLowerCase() === search.toLowerCase() ? (
        <span
          key={index}
          style={{
            backgroundColor: "#ffff00",
            color: "#000000",
            fontWeight: "bold",
            borderRadius: "2px",
            padding: "0 2px",
          }}
        >
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  const handleClearSearch = () => setSearchTerm("");
  const handleRefreshNow = () => window.location.reload();

  /** ---- Render ---- **/
  return (
    <div
      style={{
        background: "#ffffff",
        color: "#333333",
        fontFamily: "'Fira Code', monospace",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        padding: "20px",
      }}
    >
      {/* Header Bar */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "12px",
          alignItems: "center",
          background: "#f8f9fa",
          border: "1px solid #dee2e6",
          borderRadius: "8px",
          padding: "12px 16px",
          marginBottom: "15px",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        }}
      >
        {/* Visible Count */}
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <label style={{ fontWeight: "bold", fontSize: "12px", color: "#495057" }}>Show:</label>
          <input
            type="number"
            value={visibleCount}
            onChange={(e) =>
              setVisibleCount(Math.max(1, Math.min(10000, Number(e.target.value))))
            }
            style={{
              width: "80px",
              padding: "4px 8px",
              background: "#ffffff",
              color: "#333333",
              border: "1px solid #ced4da",
              borderRadius: "4px",
              textAlign: "center",
            }}
          />
        </div>

        {/* Search */}
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <label style={{ fontWeight: "bold", fontSize: "12px", color: "#495057" }}>Search:</label>
          <div style={{ position: "relative" }}>
            <input
              type="text"
              placeholder="Type to search logs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: "220px",
                padding: "4px 8px",
                background: "#ffffff",
                color: "#333333",
                border: "1px solid #ced4da",
                borderRadius: "4px",
              }}
            />
            {searchTerm && (
              <button
                onClick={handleClearSearch}
                style={{
                  position: "absolute",
                  right: "6px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "none",
                  border: "none",
                  color: "#6c757d",
                  cursor: "pointer",
                }}
              >
                ✕
              </button>
            )}
          </div>
        </div>

        {/* Filters */}
        <div style={{ display: "flex", gap: "8px" }}>
          {["ALL", "ERROR", "WARN", "INFO", "DEBUG"].map((lvl) => (
            <button
              key={lvl}
              onClick={() => setFilter(lvl)}
              style={{
                padding: "5px 10px",
                fontSize: "12px",
                borderRadius: "4px",
                border: `1px solid ${getLevelColor(lvl)}`,
                background: filter === lvl ? getLevelColor(lvl) : "transparent",
                color: filter === lvl ? "#ffffff" : getLevelColor(lvl),
                fontWeight: "bold",
                cursor: "pointer",
              }}
            >
              {lvl}
            </button>
          ))}
        </div>

        {/* Toggles */}
        <div style={{ marginLeft: "auto", display: "flex", gap: "10px" }}>
          <button
            onClick={() => setAutoRefresh(!autoRefresh)}
            style={{
              background: autoRefresh ? "#006600" : "transparent",
              color: autoRefresh ? "#ffffff" : "#006600",
              border: "1px solid #006600",
              borderRadius: "4px",
              padding: "4px 8px",
              fontSize: "12px",
              cursor: "pointer",
            }}
          >
            Auto-refresh: {autoRefresh ? "ON" : "OFF"}
          </button>

          <button
            onClick={() => setAutoScroll(!autoScroll)}
            style={{
              background: autoScroll ? "#006600" : "transparent",
              color: autoScroll ? "#ffffff" : "#006600",
              border: "1px solid #006600",
              borderRadius: "4px",
              padding: "4px 8px",
              fontSize: "12px",
              cursor: "pointer",
            }}
          >
            Auto-scroll: {autoScroll ? "ON" : "OFF"}
          </button>

          <button
            onClick={handleRefreshNow}
            style={{
              background: "#ff9900",
              color: "#ffffff",
              border: "1px solid #ff9900",
              borderRadius: "4px",
              padding: "4px 8px",
              fontSize: "12px",
              fontWeight: "bold",
              cursor: "pointer",
            }}
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Log Viewer */}
      <div
        ref={logContainerRef}
        style={{
          background: "#ffffff",
          color: "#333333",
          padding: "12px",
          flexGrow: 1,
          overflowY: "auto",
          borderRadius: "6px",
          border: "1px solid #dee2e6",
          lineHeight: "1.5",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        }}
      >
        {isLoading ? (
          <div style={{ color: "#6c757d", textAlign: "center", padding: "20px" }}>
            Loading logs...
          </div>
        ) : filteredEntries.length === 0 ? (
          <div style={{ color: "#6c757d", textAlign: "center", padding: "20px" }}>
            No log entries found.
          </div>
        ) : (
          filteredEntries.map((entry) => (
            <div
              key={entry.id}
              style={{
                marginBottom: "12px",
                paddingBottom: "8px",
                borderBottom: "1px solid #e9ecef",
              }}
            >
              <div
                style={{
                  display: "flex",
                  gap: "10px",
                  fontSize: "11px",
                  color: "#6c757d",
                  marginBottom: "4px",
                }}
              >
                <span>{entry.timestamp}</span>
                <span
                  style={{
                    color: getLevelColor(entry.level),
                    fontWeight: "bold",
                  }}
                >
                  {entry.level}
                </span>
              </div>
              <div style={{ 
                color: getLevelColor(entry.level), // Apply level color to entire log text
                fontWeight: entry.level === "ERROR" ? "bold" : "normal"
              }}>
                {highlightText(entry.text, searchTerm)}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default OpenbravoLogViewer;